<template>
    <nav v-for="entry in navigation" :key="entry.id">
        <menu v-if="showNavigation()" class="flex flex-col">
            <li class="border-b last:border-b-0">
                <template v-if="entry?.children?.length > 0">
                    <button
                        type="button"
                        class="px-4 py-3 text-sm font-semibold flex justify-between items-center gap-3 w-full"
                        :class="{
                            'text-sale sale' : getCategoryColor(entry) == 'category_color_sale',
                            'text-new new' : getCategoryColor(entry) == 'category_color_new',
                            'text-secondary-content' : !getCategoryColor(entry)
                        }"
                        @click="toggleCategories(entry.id)"
                    >
                        {{ getTranslatedProperty(entry, 'name') }}
                        <MiscIcon name="chevron-right" />
                    </button>

                    <LayoutNavigationMobileSubcategory
                        :parent="entry"
                        :children="entry.children"
                        :active-trigger="activeTrigger"
                        :cms-content="entry.customFields?.custom_cms_id ? campaignContent[entry.customFields.custom_cms_id] : null"
                        @on-close="activeTrigger = false"
                    />
                </template>

                <MiscLink
                    v-else
                    :key="entry.id"
                    no-prefetch
                    :to="getCategoryRoute(entry)"
                    class="block w-full px-4 py-3 font-semibold text-primary"
                >
                    {{ entry.name }}
                </MiscLink>
            </li>
        </menu>
    </nav>

    <div v-if="homeCmsContent" class="w-full">
        <StructurePage :content="homeCmsContent" />
    </div>

    <!-- not relevant yet -->
    <div v-if="notRelevant" class="grid grid-cols-2 gap-4 px-4 py-3">
        <a href="/fitting" class="btn btn-secondary btn-icon-left"><MiscIcon name="ruler" /> Fitting</a>
        <a href="/greenfeed" class="btn btn-secondary btn-icon-left"><MiscIcon name="flag-triangle-right" /> Greenfeed</a>
        <a href="/service" class="col-span-2 btn btn-secondary btn-icon-left"><MiscIcon name="copy" /> Service</a>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { getCategoryBreadcrumbs, getCategoryRoute, getTranslatedProperty } from '@shopware/helpers'

const props = defineProps<{
    navigation: Schemas['NavigationRouteResponse']
}>()
const { apiClient } = useShopwareContext()
const loaded = ref(false)
const notRelevant = ref(false)
const activeTrigger = ref<boolean | string>(false)
const config = useRuntimeConfig().public
const campaignContent = ref<Record<string, Schemas["CmsPage"] | null>>({})
const homeCmsContent = ref<Schemas["CmsPage"] | null>(null)

// Open active category via breadcrumb
const { breadcrumbs } = useBreadcrumbs()

// Prefix match: open the top-level section if current page is anywhere under it.
// startIndex: 1 skips "Home" from getCategoryBreadcrumbs since breadcrumbs.value never includes it.
// breadcrumbs.value items have { name, path? } — compare by name only to be robust across nav and product pages.
if ((breadcrumbs.value?.length ?? 0) > 0) {
    const currentNames = breadcrumbs.value!.map(b => b.name)
    props.navigation?.forEach((category) => {
        const catNames = getCategoryBreadcrumbs(category, { startIndex: 1 }).map(b => b.name)
        if (catNames.length > 0 && JSON.stringify(currentNames.slice(0, catNames.length)) === JSON.stringify(catNames)) {
            activeTrigger.value = category.id
        }
    })
}

onMounted(() => {
    loaded.value = true
    fetchCmsForHome()
    fetchAllCmsData()
})

function toggleCategories(trigger: string | boolean) {
    activeTrigger.value = trigger;
}

function showNavigation () {
    return loaded.value
}

const fetchAllCmsData = async () => {
    if (props?.navigation) {
        for (const category of props.navigation) {
            const cmsId = category.customFields?.custom_cms_id
            if (cmsId != null && cmsId.trim() !== '' && !campaignContent.value[cmsId]) {
                campaignContent.value[cmsId] = await fetchCms(cmsId)
            }
        }
    }
}

async function fetchCms (cmsId: string) {
    try {
        const { data } = await apiClient.invoke('readCms post /cms/{id}', {
            pathParams: {
                id: cmsId
            }, 
            body: {
                associations: {
                    media: {}
                }
            }
        })

        return data as Schemas["CmsPage"]
    } catch (error) {
        console.error(`Failed to fetch CMS content for ID ${cmsId}:`, error)
        return null
    }
}

async function fetchCmsForHome() {
    if (config.mobileMenuHomeCms == null || config.mobileMenuHomeCms.trim() === '') {
        return
    }

    try {
        const { data } = await apiClient.invoke('readCms post /cms/{id}', {
            pathParams: {
                id: config.mobileMenuHomeCms
            }, 
            body: {
                associations: {
                    media: {}
                }
            }
        })

        homeCmsContent.value = data as Schemas["CmsPage"]
    } catch (error) {
        console.error('Failed to fetch mobile menu home CMS:', error)
        homeCmsContent.value = null
    }
}

const getCategoryColor = (entry: Schemas['Category']) => {
    return (getTranslatedProperty(entry, 'customFields') as Schemas['Category']['translated']['customFields'])?.custom_category_navigation_color
}
</script>
